import { RxApiPeriodDefinition, RxApiRxType, RxTrendsApiRow } from './rx-trends.api.types';
import { RxStoredMetrics, RxStoredProductSeries } from './rx-trends.types';

/**
 * Maps the raw Rx trends payload (periods + rows) onto the shape the chart
 * consumes.
 *
 * This is the only place that understands the wire format. It is deliberately
 * pure — no Angular, no date library, no injected services — so the whole
 * conversion is unit-testable from static JSON, and so the wire shape can be
 * renegotiated without the component noticing.
 *
 * Three properties matter more than the mechanics:
 *
 * 1. **Bucket order comes from `periods`, not from re-deriving dates.** The
 *    packed contract gives every window an explicit `periodStart`, so unlike
 *    the old per-datapoint shape there is nothing to infer — the adapter just
 *    reverses newest-first to oldest-first and reads it off.
 * 2. **A row that never arrives becomes an all-`null` metric, not zeros.** A
 *    (hcp, product) pair can have a `T` row and no `N` row at all (see the
 *    sample's `hcpId: "0030626"`) — that means "we were not sent NRx for this
 *    product", not "NRx was zero every week". Explicit `0` values *inside* a
 *    present row are genuine zeros and are preserved as such.
 * 3. **An unrecognised `rxType` is dropped, loudly-ish.** Folding an unknown
 *    code into NRx or TRx would silently misreport prescription volumes.
 *
 * It produces `RxStoredProductSeries` — numbers only. Bucket *labels* are not
 * built here and not persisted: every series in the payload shares one set of
 * period windows, so building them per (hcp, product) meant the same 38
 * strings and 26 `Date` objects being constructed 150k times at territory
 * scale and then written into every document. They are derived once at render
 * from the anchors instead — see `buildWeeklyLabelsFromAnchor` below, which
 * the journey tab calls with a `DateTimeFormatsService`-backed formatter.
 */

/**
 * Fallback bucket counts, used only if `periods` is somehow missing a whole
 * period type. The real count always comes from `periods` itself.
 */
export const RX_WEEKLY_BUCKET_COUNT = 26;
export const RX_MONTHLY_BUCKET_COUNT = 12;

// ── Date helpers (UTC throughout, so a device timezone cannot shift a bucket) ──

function parseIsoDateToUtcMilliseconds(isoDate: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate ?? '');
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** `YYYY-MM-DD` -> `YYYY-MM`. */
function toMonthKey(isoDate: string): string {
  return (isoDate ?? '').slice(0, 7);
}

// ── Labels from stored anchors ───────────────────────────────────────────────

/**
 * Rebuilds bucket labels from a stored anchor.
 *
 * Labels are derived at render rather than persisted, so a user changing their
 * date format sees it applied to data already on disk, and the stored document
 * carries numbers only.
 */
export function buildWeeklyLabelsFromAnchor(
  weekAnchor: string,
  bucketCount: number,
  formatWeekLabel: (weekStartDate: string, previousWeekStartDate?: string) => string,
): string[] {
  const anchorMilliseconds = parseIsoDateToUtcMilliseconds(weekAnchor);
  if (anchorMilliseconds === null) return [];

  const millisecondsPerWeek = 7 * 24 * 60 * 60 * 1000;
  const labels: string[] = [];
  let previousWeekStartDate: string | undefined;
  for (let bucketIndex = 0; bucketIndex < bucketCount; bucketIndex++) {
    const milliseconds = anchorMilliseconds + bucketIndex * millisecondsPerWeek;
    const date = new Date(milliseconds);
    const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
    const day = `${date.getUTCDate()}`.padStart(2, '0');
    const weekStartDate = `${date.getUTCFullYear()}-${month}-${day}`;
    labels.push(formatWeekLabel(weekStartDate, previousWeekStartDate));
    previousWeekStartDate = weekStartDate;
  }
  return labels;
}

function shiftMonthKey(monthKey: string, monthsToAdd: number): string {
  const year = Number(monthKey.slice(0, 4));
  const monthIndex = Number(monthKey.slice(5, 7)) - 1 + monthsToAdd;
  const shiftedYear = year + Math.floor(monthIndex / 12);
  const shiftedMonth = ((monthIndex % 12) + 12) % 12;
  return `${shiftedYear}-${`${shiftedMonth + 1}`.padStart(2, '0')}`;
}

export function buildMonthlyLabelsFromAnchor(
  monthAnchor: string,
  bucketCount: number,
  formatMonthLabel: (monthKey: string, previousMonthKey?: string) => string,
): string[] {
  if (!monthAnchor) return [];

  const monthKeys: string[] = [];
  for (let bucketIndex = 0; bucketIndex < bucketCount; bucketIndex++) {
    monthKeys.push(shiftMonthKey(monthAnchor, bucketIndex));
  }
  return monthKeys.map((monthKey, index) =>
    formatMonthLabel(monthKey, index === 0 ? undefined : monthKeys[index - 1]),
  );
}

// ── Periods ──────────────────────────────────────────────────────────────────

/**
 * Splits `periods` into its weekly/monthly windows, ascending (oldest-first) —
 * the source sends them newest-first (`WK1` = most recent), matching the
 * order rows' `weeks`/`months` arrays arrive in.
 */
function extractAscendingPeriods(periods: RxApiPeriodDefinition[], prefix: string): RxApiPeriodDefinition[] {
  return (periods ?? [])
    .filter((period) => period?.period?.startsWith(prefix) && /^\d+$/.test(period.period.slice(prefix.length)))
    .sort((a, b) => Number(a.period.slice(prefix.length)) - Number(b.period.slice(prefix.length)))
    .reverse();
}

// ── Grouping ─────────────────────────────────────────────────────────────────

interface ProductRowsGroup {
  productId: string;
  productName: string;
  rowsByRxType: Map<RxApiRxType, RxTrendsApiRow>;
}

/**
 * hcpId -> product name -> that product's N/T rows for the hcp.
 *
 * Grouped on the name, not the id, because `productId` is only populated on
 * some rows: keying on it would file one product's N row under its GUID and
 * its T row under its name, splitting a single product into two half-empty
 * series. The id is picked up from whichever row carries one.
 */
function groupRowsByHcpThenProduct(
  rows: RxTrendsApiRow[],
): Map<string, Map<string, ProductRowsGroup>> {
  const byHcp = new Map<string, Map<string, ProductRowsGroup>>();

  for (const row of rows ?? []) {
    if (!row?.hcpId || !row?.productName) continue;
    if (row.rxType !== 'N' && row.rxType !== 'T') continue;

    let byProduct = byHcp.get(row.hcpId);
    if (!byProduct) {
      byProduct = new Map();
      byHcp.set(row.hcpId, byProduct);
    }

    const groupKey = row.productName.trim().toLowerCase();
    let group = byProduct.get(groupKey);
    if (!group) {
      group = { productId: '', productName: row.productName, rowsByRxType: new Map() };
      byProduct.set(groupKey, group);
    }

    if (row.productId && !group.productId) {
      group.productId = row.productId;
    }

    // Last write wins on a duplicate (hcp, product, rxType) — e.g. if two
    // pages ever overlapped mid-ingest.
    group.rowsByRxType.set(row.rxType, row);
  }

  return byHcp;
}

/**
 * Reverses one metric's values to oldest-first and pads/truncates to the
 * canonical bucket count. Absent row -> all `null` (unknown, not zero).
 *
 * Writes straight into a preallocated array rather than going through
 * `[...row[field]].reverse().map(...)`: that chain allocated two intermediate
 * arrays per call, and this runs four times per (hcp, product) pair — ~1.2M
 * throwaway arrays over a territory-scale ingest.
 */
function toAscendingBucketValues(
  row: RxTrendsApiRow | undefined,
  field: 'weeks' | 'months',
  bucketCount: number,
): (number | null)[] {
  const ascending: (number | null)[] = new Array(bucketCount).fill(null);
  if (!row) return ascending;

  const sourceNewestFirst = row[field] ?? [];
  // Filled from the newest end backwards. The contract promises one value per
  // period, so a length mismatch should not happen — but if it does, leaving
  // the *oldest* buckets null is the safe failure: the alternative shifts
  // every value onto the wrong week while still looking plausible.
  const valueCount = Math.min(sourceNewestFirst.length, bucketCount);
  for (let offset = 0; offset < valueCount; offset++) {
    const value = sourceNewestFirst[offset];
    ascending[bucketCount - 1 - offset] =
      typeof value === 'number' && isFinite(value) ? value : null;
  }
  return ascending;
}

// ── Entry point ──────────────────────────────────────────────────────────────

/**
 * Maps one page (or the full accumulated set) of rows to per-HCP product
 * series, keyed by `hcpId`.
 *
 * `periods` must be the same periods response the rows were paginated
 * against — see `RxTrendsDataService`'s refreshDate guard, which is what
 * makes that guarantee hold across a paged ingest.
 */
export function mapRxApiRowsToProductSeriesByHcp(
  periods: RxApiPeriodDefinition[],
  rows: RxTrendsApiRow[],
): Map<string, RxStoredProductSeries[]> {
  const weeklyPeriods = extractAscendingPeriods(periods, 'WK');
  const monthlyPeriods = extractAscendingPeriods(periods, 'M');
  const weeklyBucketCount = weeklyPeriods.length || RX_WEEKLY_BUCKET_COUNT;
  const monthlyBucketCount = monthlyPeriods.length || RX_MONTHLY_BUCKET_COUNT;
  const weekAnchor = weeklyPeriods[0]?.periodStart ?? '';
  const monthAnchor = monthlyPeriods[0] ? toMonthKey(monthlyPeriods[0].periodStart) : '';

  const seriesByHcpId = new Map<string, RxStoredProductSeries[]>();

  for (const [hcpId, productGroups] of groupRowsByHcpThenProduct(rows)) {
    const seriesForHcp: RxStoredProductSeries[] = [];

    for (const group of productGroups.values()) {
      const newRxRow = group.rowsByRxType.get('N');
      const totalRxRow = group.rowsByRxType.get('T');
      if (!newRxRow && !totalRxRow) continue;

      const weekly: RxStoredMetrics = {
        NRx: toAscendingBucketValues(newRxRow, 'weeks', weeklyBucketCount),
        TRx: toAscendingBucketValues(totalRxRow, 'weeks', weeklyBucketCount),
      };
      const monthly: RxStoredMetrics = {
        NRx: toAscendingBucketValues(newRxRow, 'months', monthlyBucketCount),
        TRx: toAscendingBucketValues(totalRxRow, 'months', monthlyBucketCount),
      };

      seriesForHcp.push({
        // Name as the id while the API leaves `productId` empty on some rows —
        // it still keys the series, it just cannot match a Dynamics pill.
        productId: group.productId || group.productName,
        productName: group.productName,
        weekAnchor,
        monthAnchor,
        weekly,
        monthly,
      });
    }

    if (!seriesForHcp.length) continue;
    seriesForHcp.sort((left, right) => left.productName.localeCompare(right.productName));
    seriesByHcpId.set(hcpId, seriesForHcp);
  }

  return seriesByHcpId;
}
